<template>
	<div class="content">
		<div class="section" v-if="item">
			<div class="section-tit">
				{{ item.title }}
				<span v-bind:class="statusClass(item.contract_status)" class="m-l--1">[{{ item.status_label }}]</span>
				<div class="right">
					<button type="button" class="btn btn-sm btn-light" @click="btnList">목록</button>
					<button type="button" class="btn btn-sm btn-secondary" v-if="perm.can_edit" @click="editPopup = true">
						정보수정
					</button>
					<button
						type="button"
						class="btn btn-sm btn-danger"
						v-if="perm.is_master || Number(item.reg_mem_uid) === myUid"
						@click="btnDelete"
					>
						삭제
					</button>
				</div>
			</div>

			<!-- ── C-1 계약 기본정보 ────────────────────────────────────────────── -->
			<table class="table type2">
				<colgroup>
					<col width="14%" /><col width="19%" /><col width="14%" /><col width="19%" />
					<col width="14%" /><col width="20%" />
				</colgroup>
				<tr>
					<th>연결 매물</th>
					<td colspan="3">
						<template v-if="item.item_deleted">
							<span class="txt-c--red">원본 매물이 삭제되었습니다.</span>
						</template>
						<template v-else-if="item.building_uid">
							{{ item.item_name }}
							<span class="txt-c--grey">{{ $normalizeAddr(item.item_jibun_addr) }}</span>
							<button type="button" class="btn btn-xsm btn-light m-l--1" @click="openOrigin">원본 상세보기</button>
						</template>
						<span v-else class="txt-c--grey">미연결</span>
					</td>
					<th>등록자</th>
					<td>{{ item.reg_mem_name }} {{ item.reg_mem_rank_name }}</td>
				</tr>
				<tr>
					<th>매매금액</th>
					<!-- 미입력(null)을 '0' 으로 보여주면 "0원 계약" 처럼 읽힌다 → '-' 로 구분한다. -->
					<td>{{ money(item.sell_price) }}</td>
					<th>매물 현재가</th>
					<td>
						<template v-if="!item.item_deleted && item.building_uid">
							{{ money(item.item_sell_price) }}
						</template>
						<span v-else>-</span>
					</td>
					<th>매물 상태</th>
					<td>
						<template v-if="!item.item_deleted && item.building_uid">
							{{ itemStateLabel }}
						</template>
						<span v-else>-</span>
					</td>
				</tr>
				<tr>
					<th>계약일</th>
					<td>{{ $dateFormat(item.contract_date) || '-' }}</td>
					<th>본계약일</th>
					<td>{{ $dateFormat(item.main_contract_date) || '-' }}</td>
					<th>잔금일</th>
					<td>
						{{ $dateFormat(item.balance_date) || '-' }}
						<span v-if="item.balance_dday !== null && item.balance_dday !== undefined" v-bind:class="ddayClass(item.balance_dday)">
							{{ ddayText(item.balance_dday) }}
						</span>
					</td>
				</tr>
				<tr>
					<th>매수인</th>
					<td>{{ item.buyer_name || '-' }}</td>
					<th>매도인</th>
					<td>{{ item.seller_name || '-' }}</td>
					<th>매물 담당</th>
					<td>{{ item.item_mgr_name || '-' }}</td>
				</tr>
				<tr>
					<th>메모</th>
					<td colspan="5" class="txt-left ct-pre">{{ item.memo || '-' }}</td>
				</tr>
			</table>

			<!-- 첨부 (계약서 등) -->
			<div class="section-tit sub mt-2">
				첨부파일 ({{ files.length }})
				<div class="right" v-if="perm.can_edit">
					<input type="file" ref="fileInput" class="d-none" multiple @change="onFileChange" />
					<button type="button" class="btn btn-sm btn-secondary" @click="$refs.fileInput.click()" :disabled="uploading">
						{{ uploading ? '업로드중…' : '파일 추가' }}
					</button>
				</div>
			</div>
			<div class="table-wrap">
				<table class="table">
					<colgroup>
						<col width="42%" /><col width="12%" /><col width="14%" /><col width="18%" /><col width="14%" />
					</colgroup>
					<tr>
						<th>파일명</th>
						<th>크기</th>
						<th>등록자</th>
						<th>등록일</th>
						<th>관리</th>
					</tr>
					<tr v-for="f in files" :key="'cf_' + f.id">
						<td class="txt-left">
							<button type="button" class="btn btn-link" @click="download(f)">{{ f.usr_name || f.org_name }}</button>
						</td>
						<td>{{ sizeText(f.size) }}</td>
						<td>{{ f.reg_mem_name }}</td>
						<td>{{ $datetimeFormat(f.reg_date) }}</td>
						<td>
							<button type="button" class="btn btn-xsm btn-danger" v-if="perm.can_edit" @click="deleteFile(f)">삭제</button>
						</td>
					</tr>
					<tr v-if="!files.length">
						<td colspan="5">첨부파일이 없습니다.</td>
					</tr>
				</table>
			</div>
		</div>

		<div class="section" v-if="item">
			<div class="tab-btn">
				<button type="button" class="btn tab" v-bind:class="{ active: tab === 'member' }" @click="tab = 'member'">
					참여자 ({{ members.length }})
				</button>
				<button type="button" class="btn tab" v-bind:class="{ active: tab === 'chk' }" @click="tab = 'chk'">
					체크리스트 ({{ chkDone }}/{{ checklist.length }})
				</button>
				<button type="button" class="btn tab" v-bind:class="{ active: tab === 'log' }" @click="tab = 'log'">
					작업내역 ({{ logs.length }})
				</button>
			</div>

			<!-- ── C-2 참여자 ───────────────────────────────────────────────────── -->
			<div class="tab-con" v-if="tab === 'member'">
				<div class="search-top" v-if="perm.can_edit">
					<div class="left">
						<label class="input-label">직원</label>
						<select v-model="newMember.memUid" class="form-control select">
							<option :value="null">선택</option>
							<option v-for="s in staffList" :key="'stf_' + s.mem_uid" :value="s.mem_uid">
								{{ s.mem_name }} {{ s.mem_rank_name ? '(' + s.mem_rank_name + ')' : '' }}
							</option>
						</select>
						<label class="input-label">역할</label>
						<select v-model="newMember.roleUid" class="form-control select">
							<option :value="null">선택</option>
							<option v-for="r in roleList" :key="'rl_' + r.ind_cfg_uid" :value="r.ind_cfg_uid">{{ r.name }}</option>
						</select>
						<button type="button" class="btn btn-sm btn-primary" @click="addMember">참여자 추가</button>
					</div>
				</div>
				<div class="txt-c--grey">※ 한 사람이 여러 역할을 겸직할 수 있습니다. 추가 시 해당 직원에게 알림이 발송됩니다.</div>
				<div class="table-wrap">
					<table class="table">
						<colgroup>
							<col width="20%" /><col width="20%" /><col width="18%" /><col width="24%" /><col width="18%" />
						</colgroup>
						<tr>
							<th>역할</th>
							<th>이름</th>
							<th>직위</th>
							<th>등록일</th>
							<th>관리</th>
						</tr>
						<tr v-for="m in members" :key="'cm_' + m.contract_member_uid">
							<td>{{ m.role_name }}</td>
							<td>{{ m.mem_name }}</td>
							<td>{{ m.mem_rank_name }}</td>
							<td>{{ $datetimeFormat(m.reg_date) }}</td>
							<td>
								<button type="button" class="btn btn-xsm btn-danger" v-if="perm.can_edit" @click="delMember(m)">삭제</button>
							</td>
						</tr>
						<tr v-if="!members.length">
							<td colspan="5">참여자가 없습니다.</td>
						</tr>
					</table>
				</div>
			</div>

			<!-- ── C-3 체크리스트 ───────────────────────────────────────────────── -->
			<div class="tab-con" v-else-if="tab === 'chk'">
				<div class="search-top" v-if="perm.can_edit">
					<div class="left">
						<label class="input-label">항목 추가</label>
						<input
							type="text"
							class="form-control"
							v-model="newChk.name"
							placeholder="체크리스트 항목명"
							@keyup.enter="addChk"
						/>
						<select v-model="newChk.mgr_mem_uid" class="form-control select">
							<option :value="null">담당자 미지정</option>
							<option v-for="s in staffList" :key="'cs_' + s.mem_uid" :value="s.mem_uid">{{ s.mem_name }}</option>
						</select>
						<button type="button" class="btn btn-sm btn-primary" @click="addChk">추가</button>
					</div>
					<div class="right">진행률 {{ chkRate }}%</div>
				</div>
				<div class="table-wrap">
					<table class="table">
						<colgroup>
							<col width="6%" /><col width="24%" /><col width="14%" /><col width="12%" />
							<col width="16%" /><col width="20%" /><col width="8%" />
						</colgroup>
						<tr>
							<th>완료</th>
							<th>항목</th>
							<th>담당자</th>
							<th>완료자</th>
							<th>완료시간</th>
							<th>메모</th>
							<th>관리</th>
						</tr>
						<tr v-for="c in checklist" :key="'ck_' + c.contract_checklist_uid">
							<td>
								<label class="input-checkbox">
									<input
										type="checkbox"
										:checked="c.done_flag === 'Y'"
										:disabled="!perm.can_edit"
										@change="toggleChk(c, $event)"
									/>
									<span class="checkbox"></span>
								</label>
							</td>
							<td class="txt-left" v-bind:class="{ 'txt-c--grey': c.done_flag === 'Y' }">{{ c.name }}</td>
							<td>
								<!-- select 은 :value 바인딩만으로는 option 이 늦게 렌더될 때 선택이 풀린다
									 (실측: 담당자가 지정돼 있는데 '미지정' 으로 보였다) → v-model 사용. -->
								<select
									v-if="perm.can_edit"
									class="form-control select sm"
									v-model="c.mgr_mem_uid"
									@change="changeMgr(c)"
								>
									<option :value="null">미지정</option>
									<option v-for="s in staffList" :key="'ms_' + c.contract_checklist_uid + '_' + s.mem_uid" :value="s.mem_uid">
										{{ s.mem_name }}
									</option>
								</select>
								<span v-else>{{ c.mgr_mem_name || '미지정' }}</span>
							</td>
							<td>{{ c.done_mem_name || '-' }}</td>
							<td>{{ c.done_date ? $datetimeFormat(c.done_date) : '-' }}</td>
							<td class="txt-left">
								<input
									v-if="perm.can_edit"
									type="text"
									class="form-control sm"
									:value="c.memo"
									@change="changeMemo(c, $event)"
								/>
								<span v-else>{{ c.memo }}</span>
							</td>
							<td>
								<button type="button" class="btn btn-xsm btn-danger" v-if="perm.can_edit" @click="delChk(c)">삭제</button>
							</td>
						</tr>
						<tr v-if="!checklist.length">
							<td colspan="7">체크리스트 항목이 없습니다.</td>
						</tr>
					</table>
				</div>
			</div>

			<!-- ── C-4 작업내역 ─────────────────────────────────────────────────── -->
			<div class="tab-con" v-else>
				<div v-if="perm.can_edit" class="ct-logform">
					<div class="search-top">
						<div class="left">
							<label class="input-label">작업 구분</label>
							<select v-model="newLog.log_type_uid" class="form-control select">
								<option :value="null">미분류</option>
								<option v-for="t in logTypeList" :key="'lt_' + t.ind_cfg_uid" :value="t.ind_cfg_uid">{{ t.name }}</option>
							</select>
							<label class="input-checkbox m-l--1">
								<input type="checkbox" v-model="newLog.notify_flag" />
								<span class="checkbox"></span>
								<span class="text">알림 발송</span>
							</label>
							<select v-model="newLog.notify_scope" class="form-control select" :disabled="!newLog.notify_flag">
								<option value="all">참여자 전체</option>
								<option value="mgr">담당자</option>
								<option value="leader">팀장</option>
								<option value="pick">직접 선택</option>
							</select>
						</div>
					</div>
					<div class="mb-1">
						<label class="input-label">참여자 지정(멘션)</label>
						<label
							class="input-checkbox m-r--1"
							v-for="m in memberUniq"
							:key="'lm_' + m.mem_uid"
						>
							<input type="checkbox" :value="m.mem_uid" v-model="newLog.target_mem_uids" />
							<span class="checkbox"></span>
							<span class="text">{{ m.mem_name }}</span>
						</label>
						<span v-if="!memberUniq.length" class="txt-c--grey">참여자를 먼저 등록하세요.</span>
					</div>
					<textarea class="form-control" rows="3" v-model="newLog.memo" placeholder="작업 내용을 입력하세요."></textarea>
					<div class="txt-right mt-1">
						<span class="txt-c--grey m-r--1">※ 지정된 참여자에게는 알림 옵션과 무관하게 알림이 발송됩니다.</span>
						<button type="button" class="btn btn-sm btn-primary" @click="addLog" :disabled="savingLog">등록</button>
					</div>
				</div>
				<div class="table-wrap mt-2">
					<table class="table">
						<colgroup>
							<col width="14%" /><col width="12%" /><col width="14%" /><col width="42%" /><col width="18%" />
						</colgroup>
						<tr>
							<th>일시</th>
							<th>구분</th>
							<th>작성자</th>
							<th>내용</th>
							<th>지정 참여자</th>
						</tr>
						<tr v-for="l in logs" :key="'cl_' + l.contract_log_uid">
							<td>{{ $datetimeFormat(l.reg_date) }}</td>
							<td>{{ l.log_type_name || '-' }}</td>
							<td>{{ l.reg_mem_name }}</td>
							<td class="txt-left ct-pre">{{ l.memo }}</td>
							<td>
								{{ l.target_mem_names || '-' }}
								<span v-if="l.notify_flag === 'Y'" class="txt-c--blue d-block">알림 발송</span>
							</td>
						</tr>
						<tr v-if="!logs.length">
							<td colspan="5">작업내역이 없습니다.</td>
						</tr>
					</table>
				</div>
			</div>
		</div>

		<ContractForm
			v-if="editPopup && item"
			:contract-uid="contractUid"
			:initial="item"
			@close="editPopup = false"
			@saved="onSaved"
		/>
	</div>
</template>

<script>
import ContractForm from '@/components/ContractForm.vue';

export default {
	name: 'ContractDetail',
	components: { ContractForm },
	data() {
		return {
			contractUid: this.$route.params.id,
			item: null,
			members: [],
			checklist: [],
			logs: [],
			files: [],
			perm: { can_edit: false, is_member: false, is_master: false },
			roleList: [],
			logTypeList: [],
			staffList: [],
			tab: 'member',
			editPopup: false,
			uploading: false,
			savingLog: false,
			newMember: { memUid: null, roleUid: null },
			newChk: { name: '', mgr_mem_uid: null },
			newLog: { log_type_uid: null, memo: '', notify_flag: false, notify_scope: 'all', target_mem_uids: [] },
		};
	},
	computed: {
		myUid() {
			return Number(this.$store.getters.getUserInfo.mem_uid);
		},
		chkDone() {
			return this.checklist.filter(c => c.done_flag === 'Y').length;
		},
		itemStateLabel() {
			if (!this.item || !this.item.item_sell_status) return '-';
			// 임대 매물은 상태 라벨 표기가 다르다($ITEM_STATE2: sell=임대완료).
			const map = this.item.item_kind === 'rent_building' ? this.$ITEM_STATE2 : this.$ITEM_STATE;
			return map[this.item.item_sell_status] || this.item.item_sell_status;
		},
		chkRate() {
			if (!this.checklist.length) return 0;
			return Math.round((this.chkDone / this.checklist.length) * 100);
		},
		// 겸직(같은 사람이 여러 역할)일 때 멘션 체크박스가 중복되지 않게 한 번만 노출한다.
		memberUniq() {
			const seen = new Set();
			const out = [];
			this.members.forEach(m => {
				if (seen.has(m.mem_uid)) return;
				seen.add(m.mem_uid);
				out.push(m);
			});
			return out;
		},
	},
	created() {
		this.load();
		this.loadCodes();
	},
	methods: {
		load() {
			this.$apiGET('/admin/api/contract/item?id=' + this.contractUid).then(re => {
				if (!re || !re.item) return;
				this.item = re.item;
				this.members = re.members || [];
				this.checklist = re.checklist || [];
				this.logs = re.logs || [];
				this.files = re.files || [];
				this.perm = re.perm || this.perm;
			});
		},
		loadCodes() {
			this.$apiGET('/admin/api/contract/code?part=role').then(re => {
				if (Array.isArray(re)) this.roleList = re;
			});
			this.$apiGET('/admin/api/contract/code?part=log_type').then(re => {
				if (Array.isArray(re)) this.logTypeList = re;
			});
			this.$apiGET('/admin/api/contract/staff').then(re => {
				if (Array.isArray(re)) this.staffList = re;
			});
		},
		btnList() {
			this.$router.push({ name: 'ContractAdmin' });
		},
		onSaved() {
			this.editPopup = false;
			this.load();
		},
		btnDelete() {
			if (!confirm('이 계약을 삭제하시겠습니까? 참여자·체크리스트·작업내역이 함께 보이지 않게 됩니다.')) return;
			this.$apiPOST('/admin/api/contract/delete', { contractUid: this.contractUid }).then(re => {
				if (re === false) return;
				this.btnList();
			});
		},
		openOrigin() {
			const target = this.item.item_kind === 'rent_building' ? '/LeaseMapDetail/' : '/MapDetail/';
			const w = window.screen.availWidth;
			const h = window.screen.availHeight;
			window.open(target + this.item.building_uid, '', `width=${w}, height=${h}, resizable=no, status=no`);
		},
		statusClass(s) {
			if (s === 'done') return 'txt-c--grey';
			if (s === 'cancel') return 'txt-c--red';
			if (s === 'delay') return 'txt-c--orange';
			if (s === 'ready') return 'txt-c--red';
			return 'txt-c--blue';
		},
		ddayText(d) {
			const n = Number(d);
			if (!Number.isFinite(n)) return '';
			if (n === 0) return 'D-DAY';
			return n > 0 ? `D-${n}` : `${Math.abs(n)}일 경과`;
		},
		ddayClass(d) {
			const n = Number(d);
			if (!Number.isFinite(n)) return '';
			if (n < 0) return 'txt-c--grey';
			if (n <= 7) return 'txt-c--red';
			if (n <= 30) return 'txt-c--orange';
			return 'txt-c--grey';
		},
		money(v) {
			if (v === null || v === undefined || v === '') return '-';
			return this.$formatMoney(v, this.$MONEY_FORMAT_TYPE.MUCH);
		},
		sizeText(size) {
			const n = Number(size) || 0;
			if (n >= 1024 * 1024) return (n / 1024 / 1024).toFixed(1) + 'MB';
			if (n >= 1024) return Math.round(n / 1024) + 'KB';
			return n + 'B';
		},

		// ── 참여자 ────────────────────────────────────────────────────────────
		addMember() {
			if (!this.newMember.memUid || !this.newMember.roleUid) {
				alert('직원과 역할을 모두 선택하세요.');
				return;
			}
			this.$apiPOST('/admin/api/contract/member/add', {
				contractUid: this.contractUid,
				memUid: this.newMember.memUid,
				roleUid: this.newMember.roleUid,
			}).then(re => {
				if (re === false) return;
				if (re && re.inserted === 0) alert('이미 같은 역할로 등록된 참여자입니다.');
				this.newMember = { memUid: null, roleUid: null };
				this.load();
			});
		},
		delMember(m) {
			if (!confirm(`${m.mem_name}(${m.role_name}) 참여자를 삭제하시겠습니까?`)) return;
			this.$apiPOST('/admin/api/contract/member/delete', { memberUid: m.contract_member_uid }).then(re => {
				if (re === false) return;
				this.load();
			});
		},

		// ── 체크리스트 ────────────────────────────────────────────────────────
		addChk() {
			if (!this.newChk.name || !this.newChk.name.trim()) {
				alert('항목명을 입력하세요.');
				return;
			}
			this.$apiPOST('/admin/api/contract/checklist/add', {
				contractUid: this.contractUid,
				name: this.newChk.name.trim(),
				mgr_mem_uid: this.newChk.mgr_mem_uid,
			}).then(re => {
				if (re === false) return;
				this.newChk = { name: '', mgr_mem_uid: null };
				this.load();
			});
		},
		toggleChk(c, ev) {
			const flag = ev.target.checked ? 'Y' : 'N';
			this.$apiPOST('/admin/api/contract/checklist/toggle', {
				chkUid: c.contract_checklist_uid,
				flag,
			}).then(re => {
				if (re === false) {
					ev.target.checked = c.done_flag === 'Y'; // 실패 시 체크 상태 원복
					return;
				}
				this.load();
			});
		},
		changeMgr(c) {
			this.$apiPOST('/admin/api/contract/checklist/update', {
				chkUid: c.contract_checklist_uid,
				name: c.name,
				mgr_mem_uid: c.mgr_mem_uid,
				memo: c.memo,
			}).then(re => {
				if (re === false) return;
				this.load();
			});
		},
		changeMemo(c, ev) {
			this.$apiPOST('/admin/api/contract/checklist/update', {
				chkUid: c.contract_checklist_uid,
				name: c.name,
				mgr_mem_uid: c.mgr_mem_uid,
				memo: ev.target.value,
			}).then(re => {
				if (re === false) return;
				this.load();
			});
		},
		delChk(c) {
			if (!confirm(`체크리스트 "${c.name}" 항목을 삭제하시겠습니까?`)) return;
			this.$apiPOST('/admin/api/contract/checklist/delete', { chkUid: c.contract_checklist_uid }).then(re => {
				if (re === false) return;
				this.load();
			});
		},

		// ── 작업내역 ──────────────────────────────────────────────────────────
		addLog() {
			if (!this.newLog.memo || !this.newLog.memo.trim()) {
				alert('작업 내용을 입력하세요.');
				return;
			}
			this.savingLog = true;
			this.$apiPOST('/admin/api/contract/log/add', {
				contractUid: this.contractUid,
				log_type_uid: this.newLog.log_type_uid,
				memo: this.newLog.memo.trim(),
				notify_flag: this.newLog.notify_flag ? 'Y' : 'N',
				notify_scope: this.newLog.notify_scope,
				target_mem_uids: this.newLog.target_mem_uids.join(','),
			}).then(re => {
				this.savingLog = false;
				if (re === false) return;
				this.newLog = { log_type_uid: null, memo: '', notify_flag: false, notify_scope: 'all', target_mem_uids: [] };
				this.load();
			});
		},

		// ── 첨부 ──────────────────────────────────────────────────────────────
		// 여러 파일을 한 번에 골라도 순차 업로드한다(서버 multer 는 요청당 1개).
		async onFileChange(ev) {
			const files = ev.target.files ? Array.from(ev.target.files) : [];
			ev.target.value = '';
			if (!files.length) return;
			this.uploading = true;
			const failed = [];
			let authAborted = false;
			try {
				for (const file of files) {
					// $apiFILE 은 (path, bid, fileObj, fileName) 시그니처로 form 에 bid/fileName/file 을 담는다.
					// 백엔드가 req.body.contractUid || req.body.bid 를 모두 받으므로 그대로 재사용한다.
					const re = await this.$apiFILE('/admin/api/contract/file/add', this.contractUid, file, file.name);
					// 401 이면 $apiFILE 이 undefined 를 돌려주고 이미 로그인 화면으로 이동시켰다.
					// 계속 돌리면 로그인 화면에서 남은 업로드와 목록 갱신이 이어진다 → 즉시 중단.
					if (re === undefined) {
						authAborted = true;
						break;
					}
					if (re !== true) failed.push(file.name);
				}
			} finally {
				this.uploading = false;
			}
			if (authAborted) return;
			// 성공분은 목록에 바로 보이므로 별도 알림 없이 갱신만 한다.
			if (failed.length && failed.length !== files.length) {
				alert(`첨부파일 ${failed.length}개를 올리지 못했습니다.\n(${failed.join(', ')})`);
			}
			this.load();
		},
		download(f) {
			// $apiDOWN 은 arraybuffer 를 돌려준다(Blob 아님).
			this.$apiDOWN('/admin/api/contract/file/item?id=' + f.id, f.type).then(re => {
				if (!re) return;
				const url = window.URL.createObjectURL(new Blob([re], { type: f.type || 'application/octet-stream' }));
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', f.usr_name || f.org_name || 'file');
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			});
		},
		deleteFile(f) {
			if (!confirm('첨부파일을 삭제하시겠습니까?')) return;
			this.$apiPOST('/admin/api/contract/file/delete', { id: f.id }).then(re => {
				if (re === false) return;
				this.load();
			});
		},
	},
};
</script>

<style lang="scss">
.ct-pre {
	white-space: pre-wrap;
	word-break: break-all;
}
.ct-logform {
	padding: 8px;
	background: #f7f8fa;
	border: 1px solid #e5e7eb;
}
</style>
